/**
 * PERSON DETAIL — Layout 3 colonne (da wireframe "Contatto").
 *
 * Tutti i campi scalari della sidebar sinistra sono editabili inline via
 * <EditableField>: doppio click per entrare in edit, blur/Enter per committare,
 * Esc per cancellare. Le liste email e phones sono gestite da editor dedicati
 * (<EmailListEditor>, <PhoneListEditor>) che usano API sub-entity polimorfiche.
 *
 * Campi readonly: lastInteractionDate (derivato backend).
 * Il pulsante "Modifica" (che apre il form classico) e' condizionato al
 * feature flag VITE_ENABLE_EDIT_FORMS.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import AddTaskIcon from '@mui/icons-material/AddTask';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EventIcon from '@mui/icons-material/Event';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TaskForm from '../task/TaskForm';
import NoteFormModal from '../note/NoteFormModal';
import CallFormModal from '../call/CallFormModal';
import EmailFormModal from '../email/EmailFormModal';
import MeetingFormModal from '../meeting/MeetingFormModal';
import LinkedinPostFormModal from '../linkedinPost/LinkedinPostFormModal';
import WhatsAppFormModal from '../whatsAppMessage/WhatsAppFormModal';
import { useWhatsAppEnabled } from '../whatsAppMessage/useWhatsAppEnabled';
import { fetchWhatsAppSessions } from '../whatsAppSession/whatsAppSessionThunks';
import PersonForm from './PersonForm';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchPerson, updatePerson } from './personThunks';
import { clearCurrentPerson } from './personSlice';
import { fetchRelationships } from '../relationship/relationshipThunks';
import { RelationPanels } from '../../components/RelationPanels';
import { FairContactsPanel } from '../../components/FairContactsPanel';
import { useIsCoreItemEnabled } from '../../plugin/useCoreItems';
import { ActivityTimeline } from '../../components/ActivityTimeline';
import { DetailTabs } from '../../components/DetailTabs';
import { isValidActivitySection } from '../../components/activitySections';
import { useActivityFocus } from '../../components/useActivityFocus';
import { useEntityActivities } from '../../components/useEntityActivities';
import { PipelinePlacement } from '../../components/PipelinePlacement';
import { useSlotContributors } from '../../plugin/useSlotContributors';
import { EditableField, type EditableValue, type LookupFreeSoloValue } from '../../components/EditableField';
import { PersonAvatar } from '../../components/PersonAvatar';
import { searchPersonSourcesAsLookup } from '../personSource/personSourceLookup';
import { searchPersonChannelsAsLookup } from '../personChannel/personChannelLookup';
import { useCurrentGroupId } from '../../auth/authSelectors';
import { EmailListEditor } from '../../components/EmailListEditor';
import { PhoneListEditor } from '../../components/PhoneListEditor';
import { useToast } from '../../components/useToast';
import { getEntityFqn } from '../../entityRegistry/entityRegistry';
import { useEnabledPluginEntityTypes } from '../../plugin/useEnabledEntityTypes';
import { LEAD_STATUSES, MARKETING_STATUSES } from './personTypes';
import type { PersonCreatePayload, PersonUpdatePayload } from './personTypes';
import { ACTIVITY_TO_TASK_TYPE, type TaskType } from '../task/taskTypes';

/** Drawer preset aperto da un'azione rapida della sidebar Contatto. */
type QuickDrawer = { kind: 'task'; initialType?: TaskType };

export default function PersonDetail() {
  const { id, section, activityId } = useParams<{
    id: string;
    section?: string;
    activityId?: string;
  }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const toast = useToast();
  const enabledPluginEntityTypes = useEnabledPluginEntityTypes();
  const { currentItem: person } = useAppSelector((state) => state.persons);
  const [quickDrawer, setQuickDrawer] = useState<QuickDrawer | null>(null);
  const [noteFormOpen, setNoteFormOpen] = useState(false);
  const [callFormOpen, setCallFormOpen] = useState(false);
  const [emailFormOpen, setEmailFormOpen] = useState(false);
  const [meetingFormOpen, setMeetingFormOpen] = useState(false);
  const [linkedinFormOpen, setLinkedinFormOpen] = useState(false);
  const [whatsAppFormOpen, setWhatsAppFormOpen] = useState(false);
  // Il pulsante WhatsApp c'è solo se la company ha un numero business, come la pagina.
  const whatsAppEnabled = useWhatsAppEnabled();
  // Contatti rapidi spenti dalla pagina «Voci del CRM» (FILO-53): via anche il pannello delle fiere,
  // che porta alle loro pagine.
  const rapidContactsEnabled = useIsCoreItemEnabled('rapid-contacts');
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  // Ancora per il menu "Altro" (lista tipologie task non coperte dai 4 bottoni
  // primari: email, riunione, nota).
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);
  const numId = id ? Number(id) : 0;
  const activities = useEntityActivities('Person', numId);
  const groupId = useCurrentGroupId();

  // Sotto-tab attività pilotati dall'URL (/contacts/:id/:section): l'URL è la
  // fonte di verità del filtro, non lo stato interno di useEntityActivities.
  // Tab contribuite dai plugin per questa entita' (TS-107). Le legge la scheda e
  // non la timeline perche' servono anche qui sotto, alla guardia del redirect:
  // uno slug contribuito e' una sezione valida quanto 'notes'.
  const activityTabs = useSlotContributors('Person', 'detail-center:activity-tab');
  // Colonna destra: i plugin possono metterci un riquadro sopra o sotto i
  // pannelli relazione. Stessa coppia di slot del preventivo — vedi
  // EntityDetailSlot in @crm/contracts, dove sono passati quando anche questa
  // scheda ha cominciato a renderli.
  const rightTopContributors = useSlotContributors('Person', 'detail-right:top');
  const rightBottomContributors = useSlotContributors('Person', 'detail-right:bottom');
  // Sotto l'anagrafica: riquadri che completano i dati del record.
  const leftBottomContributors = useSlotContributors('Person', 'detail-left:bottom');
  // Deep-link a un'attività (commento o ?activity= dalla ricerca): id dell'item su
  // cui espandere/scrollare. Il commento specifico lo apre ActivityTools.
  const focusItemId = useActivityFocus(section, activityId);
  const handleSectionChange = useCallback(
    (slug: string | undefined) => {
      navigate(slug ? `/contacts/${id}/${slug}` : `/contacts/${id}`);
    },
    [id, navigate],
  );

  // Slug sconosciuto (es. /contacts/101/foo) -> redirect a "Tutte" (rotta base).
  useEffect(() => {
    if (section != null && !isValidActivitySection(section)
      && !activityTabs.some((c) => c.slug === section))
      navigate(`/contacts/${id}`, { replace: true });
  }, [section, id, navigate, activityTabs]);

  // Fetcher per l'autocomplete `Fonte` (variant lookupFreeSolo): closure
  // groupId-bound sopra il helper condiviso. Stable identity via useCallback
  // (richiesto dal contratto di useAsyncAutocomplete dentro EditableField).
  const fetchPersonSourceOptions = useCallback(
    (query: string) => searchPersonSourcesAsLookup(groupId, query),
    [groupId],
  );

  const fetchPersonChannelOptions = useCallback(
    (query: string) => searchPersonChannelsAsLookup(groupId, query),
    [groupId],
  );

  useEffect(() => {
    if (numId) dispatch(fetchPerson(numId));
    return () => {
      dispatch(clearCurrentPerson());
    };
  }, [numId, dispatch]);

  // Le relazioni non si fetchano qui: le possiede chi le usa (RelationPanels e
  // la timeline, via useRelationshipsSource). Questo resta come refetch
  // esplicito dopo una creazione che aggiunge un link (i drawer qui sotto).
  function refreshRelations() {
    const fqn = getEntityFqn('Person');
    if (numId && fqn) dispatch(fetchRelationships({ className: fqn, classPK: numId }));
  }

  // Ricarica le sessioni WhatsApp dopo un invio, così la conversazione aggiornata
  // ricompare in timeline (le sessioni sono caricate da useEntityActivities).
  const refreshWhatsAppSessions = useCallback(() => {
    if (numId && groupId) {
      dispatch(fetchWhatsAppSessions({ personId: numId }));
    }
  }, [numId, groupId, dispatch]);

  if (!person) return <p>{t('common.loading')}</p>;

  // Factory per commit inline di un singolo campo scalare della Person.
  // Dispatch updatePerson con Partial<PersonCreatePayload> + personId.
  const commitField =
    (field: keyof PersonCreatePayload) =>
    async (newValue: EditableValue) => {
      try {
        const payload = { personId: person.personId, [field]: newValue } as PersonUpdatePayload;
        await dispatch(updatePerson(payload)).unwrap();
        toast({ severity: 'success', message: t('common.saved') });
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        throw err;
      }
    };

  /**
   * La collocazione in bacheca non passa da `commitField`: quello è tipizzato su
   * `PersonCreatePayload`, e `currentStateId` non è un campo di creazione — sta solo
   * nell'`UpdatePayload`, perché la colonna si sceglie su un contatto che esiste già.
   */
  const commitCurrentStateId = async (pipelineStateId: number) => {
    try {
      await dispatch(updatePerson({ personId: person.personId, currentStateId: pipelineStateId }))
        .unwrap();
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
      throw err;
    }
  };

  const fullName = `${person.firstName} ${person.lastName}`.trim();

  return (
    <div className="detail-page">
      {/* Breadcrumb fuori dalle colonne: torna ai contatti + nome del contatto
          corrente (stile mockup: "Contatti / Nome Cognome"). */}
      <nav className="detail-crumb" aria-label={t('person.title')}>
        <Link to="/contacts" className="detail-crumb__back">&larr; {t('person.title')}</Link>
        <span className="detail-crumb__sep">/</span>
        <span className="detail-crumb__current">{fullName}</span>
      </nav>

      {/* `--stack`: sotto i 1440px le tre colonne diventano due — l'anagrafica
          resta a sinistra della sua misura, la timeline prende tutto il resto e
          i pannelli relazione si incolonnano sotto (vedi _detail.scss). */}
      <div className="detail-layout detail-layout--stack">
        {/* LEFT SIDEBAR — Info contatto */}
        <div className="detail-left">

        {/* Header identità (card): avatar + nome (firstName/lastName editabili
            inline, resi come titolo serif) + email primaria. */}
        <section className="card identity">
          <div className="id-head">
            <PersonAvatar firstName={person.firstName} lastName={person.lastName} size="lg" />
            <div className="id-main">
              <div className="id-name">
                <EditableField
                  value={person.firstName}
                  variant={{ kind: 'text' }}
                  required
                  onCommit={commitField('firstName')}
                  label={t('person.firstName')}
                />
                <EditableField
                  value={person.lastName}
                  variant={{ kind: 'text' }}
                  required
                  onCommit={commitField('lastName')}
                  label={t('person.lastName')}
                />
              </div>
              {person.emailAddresses[0] && (() => {
                const emailAddress = person.emailAddresses.find((e) => e.primary)?.address ?? person.emailAddresses[0].address;
                return (
                  <div className="entity-email-row id-email">
                    <span className="text-sm text-primary">{emailAddress}</span>
                    <IconButton
                      size="small"
                      aria-label={t('common.copy')}
                      onClick={async () => {
                        await navigator.clipboard.writeText(emailAddress);
                        toast({ severity: 'success', message: t('common.copied') });
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Azioni rapide DENTRO la card identità (5 bolle + menu "Altro").
              Tutti aprono il TaskForm con initialType specifico: è la categoria
              del task a cambiare, non il flow.
              Ordine: whatsapp | linkedin | chiamata | task | altro. */}
          {(() => {
          const primaryPhone = person.phones.find((p) => p.primary)?.number ?? person.phones[0]?.number;
          return (
            <div className="action-row">
              {/* TS-71: invio messaggio WhatsApp al contatto. Disabilitato se il
                  contatto non ha numeri (la combobox del modal usa person.phones);
                  assente se la company non ha numeri WhatsApp. */}
              {whatsAppEnabled && <div className="action-row__item">
                <IconButton
                  size="small"
                  className="action-row__btn"
                  aria-label={t('person.actions.whatsapp')}
                  disabled={!primaryPhone}
                  onClick={() => setWhatsAppFormOpen(true)}
                >
                  <WhatsAppIcon fontSize="small" />
                </IconButton>
                <span className="action-row__label">{t('person.actions.whatsapp')}</span>
              </div>}
              <div className="action-row__item">
                <IconButton
                  size="small"
                  className="action-row__btn"
                  aria-label={t('person.actions.linkedin')}
                  onClick={() => setLinkedinFormOpen(true)}
                >
                  <LinkedInIcon fontSize="small" />
                </IconButton>
                <span className="action-row__label">{t('person.actions.linkedin')}</span>
              </div>
              <div className="action-row__item">
                <IconButton
                  size="small"
                  className="action-row__btn"
                  aria-label={t('person.actions.phone')}
                  disabled={!primaryPhone}
                  onClick={() => setCallFormOpen(true)}
                >
                  <PhoneOutlinedIcon fontSize="small" />
                </IconButton>
                <span className="action-row__label">{t('person.actions.phone')}</span>
              </div>
              <div className="action-row__item">
                <IconButton
                  size="small"
                  className="action-row__btn"
                  aria-label={t('person.actions.task')}
                  onClick={() => setQuickDrawer({ kind: 'task' })}
                >
                  <AddTaskIcon fontSize="small" />
                </IconButton>
                <span className="action-row__label">{t('person.actions.task')}</span>
              </div>
              <div className="action-row__item">
                <IconButton
                  size="small"
                  className="action-row__btn"
                  aria-label={t('person.actions.more')}
                  onClick={(e) => setMoreAnchor(e.currentTarget)}
                >
                  <MoreHorizIcon fontSize="small" />
                </IconButton>
                <span className="action-row__label">{t('person.actions.more')}</span>
              </div>
              {/* Context menu con le tipologie rimanenti (non coperte da
                  bottoni primari). Ancorato al bottone "Altro". */}
              <Menu
                anchorEl={moreAnchor}
                open={Boolean(moreAnchor)}
                onClose={() => setMoreAnchor(null)}
              >
                {/* Email ora entita' dedicata: voce abilitata, apre EmailFormModal. */}
                <MenuItem
                  onClick={() => {
                    setEmailFormOpen(true);
                    setMoreAnchor(null);
                  }}
                >
                  <MailOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
                  {t('task.types.email')}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setMeetingFormOpen(true);
                    setMoreAnchor(null);
                  }}
                >
                  <EventIcon fontSize="small" style={{ marginRight: 8 }} />
                  {t('task.types.meetings')}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setNoteFormOpen(true);
                    setMoreAnchor(null);
                  }}
                >
                  <NoteAddIcon fontSize="small" style={{ marginRight: 8 }} />
                  {t('task.types.notes')}
                </MenuItem>
              </Menu>
            </div>
          );
        })()}
        </section>

        <section className="card panels">
        <Accordion defaultExpanded disableGutters elevation={0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography className="relation-title">{t('person.info')}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <EmailListEditor className="Person" classPK={person.personId} emailAddresses={person.emailAddresses} />
            <PhoneListEditor className="Person" classPK={person.personId} phones={person.phones} />

            <InfoLine label={t('person.externalCode')}>
              <EditableField
                value={person.externalCode}
                variant={{ kind: 'text' }}
                readOnly
                onCommit={commitField('externalCode')}
                label={t('person.externalCode')}
              />
            </InfoLine>

            <InfoLine label={t('person.locationLabel')}>
              <EditableField
                value={person.location}
                variant={{ kind: 'text' }}
                onCommit={commitField('location')}
                label={t('person.locationLabel')}
              />
            </InfoLine>

            <InfoLine label={t('person.source')}>
              {/* Variant lookupFreeSolo: dropdown delle PersonSource esistenti
                  (ricerca server-side via personSourceApi) + free-text per
                  creare implicitamente nuove voci (find-or-create lato backend).
                  Il draft è LookupFreeSoloValue {id, name}; nel commit decidiamo
                  cosa inviare al backend in base a id>0 vs name solo. */}
              <EditableField
                value={{ id: person.personSourceId, name: person.personSource }}
                variant={{ kind: 'lookupFreeSolo', fetcher: fetchPersonSourceOptions }}
                onCommit={async (next: EditableValue) => {
                  const v = next as LookupFreeSoloValue;
                  try {
                    await dispatch(updatePerson({
                      personId: person.personId,
                      personSourceId: v.id,
                      personSource: v.name,
                    })).unwrap();
                    toast({ severity: 'success', message: t('common.saved') });
                  } catch (err) {
                    toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
                    throw err;
                  }
                }}
                label={t('person.source')}
              />
            </InfoLine>

            <InfoLine label={t('person.channel')}>
              {/* Stessa logica di personSource: variant lookupFreeSolo +
                  find-or-create lato backend. NON required. */}
              <EditableField
                value={{ id: person.personChannelId, name: person.personChannel }}
                variant={{ kind: 'lookupFreeSolo', fetcher: fetchPersonChannelOptions }}
                onCommit={async (next: EditableValue) => {
                  const v = next as LookupFreeSoloValue;
                  try {
                    await dispatch(updatePerson({
                      personId: person.personId,
                      personChannelId: v.id,
                      personChannel: v.name,
                    })).unwrap();
                    toast({ severity: 'success', message: t('common.saved') });
                  } catch (err) {
                    toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
                    throw err;
                  }
                }}
                label={t('person.channel')}
              />
            </InfoLine>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded disableGutters elevation={0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography className="relation-title">{t('person.statusHeading')}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <InfoLine label={t('person.marketingStatus')}>
              <EditableField
                value={person.marketingStatus}
                variant={{ kind: 'select', options: MARKETING_STATUSES }}
                required
                onCommit={commitField('marketingStatus')}
                label={t('person.marketingStatus')}
              />
            </InfoLine>

            <InfoLine label={t('person.leadStatus')}>
              <EditableField
                value={person.leadStatus}
                variant={{ kind: 'select', options: LEAD_STATUSES }}
                required
                onCommit={commitField('leadStatus')}
                label={t('person.leadStatus')}
              />
            </InfoLine>

            <InfoLine label={t('person.lastInteraction')}>
              <EditableField
                value={person.lastInteractionDate?.split('T')[0] ?? null}
                variant={{ kind: 'date' }}
                readOnly
                onCommit={async () => { /* readonly */ }}
                label={t('person.lastInteraction')}
              />
            </InfoLine>

            {/* Collocazione in bacheca (TS-117). Sta in questa sezione perché è uno stato
                commerciale come gli altri, ma IN FONDO e con le sue etichette esplicite
                ("Pipeline" / "Stato pipeline"): lo stato di pipeline e lo stato lead sono due assi
                indipendenti, e affiancarli senza dirlo li farebbe leggere come alternative dello
                stesso campo. Non compare affatto se non esistono pipeline dei contatti. */}
            <PipelinePlacement
              fqn={getEntityFqn('Person')}
              currentStateId={person.currentStateId}
              onCommit={commitCurrentStateId}
            />
          </AccordionDetails>
        </Accordion>
        </section>

        {leftBottomContributors.map((c) => (
          <c.component key={c.key} entityId={person.personId} />
        ))}

        {/* Pillola `q-btn`, la stessa delle azioni del preventivo: il pulsante
            "Modifica" deve essere identico nelle tre schede di dettaglio. */}
        {import.meta.env.VITE_ENABLE_EDIT_FORMS === 'true' && (
          <div className="q-actions mt-lg">
            <button type="button" className="q-btn q-btn--primary" onClick={() => setEditDrawerOpen(true)}>
              {t('common.edit')}
            </button>
          </div>
        )}
      </div>

      {/* CENTER — La tab core è "Attività" (timeline + sotto-tab per tipo, gestiti
          da ActivityTimeline). Le sezioni in più non si aggiungono qui: le
          contribuiscono i plugin nello slot `detail-center:tab`, e a comporle è
          DetailTabs (TS-107). */}
      <div className="detail-center">
        <DetailTabs
          entityType="Person"
          entityId={person.personId}
          coreLabel={t('person.tabs.activities')}
        >
          {activities.loading ? (
            <p className="text-muted">{t('person.loadingActivities')}</p>
          ) : (
            <ActivityTimeline
              items={activities.items}
              filters={activities.filters}
              section={section}
              onSectionChange={handleSectionChange}
              activityTabs={activityTabs}
              entityId={person.personId}
              onExpandItem={activities.onExpandItem}
              focusItemId={focusItemId}
              emptyMessage={t('person.emptyActivities')}
              searchable
              getCreateAction={(type) => {
                const taskType = ACTIVITY_TO_TASK_TYPE[type];
                if (!taskType) return null;
                // TS-21 Macroarea 2: Note ha entita' dedicata, apre NoteFormModal.
                if (type === 'nota') {
                  return {
                    label: t(`activity.newItem.${type}`),
                    onClick: () => setNoteFormOpen(true),
                  };
                }
                // TS-21 Macroarea 3: Call ha entita' dedicata.
                if (type === 'chiamata') {
                  return {
                    label: t(`activity.newItem.${type}`),
                    onClick: () => setCallFormOpen(true),
                  };
                }
                // Email ora entita' dedicata: apre EmailFormModal.
                if (type === 'email') {
                  return {
                    label: t(`activity.newItem.${type}`),
                    onClick: () => setEmailFormOpen(true),
                  };
                }
                // TS-21 Macroarea 4: Meeting ha entita' dedicata.
                if (type === 'riunione') {
                  return {
                    label: t(`activity.newItem.${type}`),
                    onClick: () => setMeetingFormOpen(true),
                  };
                }
                // TS-32: LinkedinPost ha entita' dedicata, apre LinkedinPostFormModal.
                if (type === 'linkedin') {
                  return {
                    label: t(`activity.newItem.${type}`),
                    onClick: () => setLinkedinFormOpen(true),
                  };
                }
                return {
                  label: t(`activity.newItem.${type}`),
                  onClick: () => setQuickDrawer({ kind: 'task', initialType: taskType }),
                };
              }}
            />
          )}
        </DetailTabs>
      </div>

      {/* RIGHT SIDEBAR — Relation panels. L'ordine e' esplicito perche' la
          pagina ha un layout studiato: aziende prima, contatti in fondo, i
          target dei plugin in mezzo. Elencare i plugin qui non li hardcoda —
          la lista viene dai plugin abilitati, quindi uno nuovo compare da solo
          e uno spento sparisce; RelationPanels scarta i tipi non disponibili.

          'Quotation' va nominato ESPLICITAMENTE (TS-87): finché il preventivo
          era di un plugin arrivava da useEnabledPluginEntityTypes, che per
          costruzione ritorna solo i tipi dei plugin. Diventando core sparirebbe
          da qui in silenzio, e con lui il pannello "Preventivi". */}
      <div className="detail-right">
        {rightTopContributors.map((c) => (
          <c.component key={c.key} entityId={person.personId} />
        ))}

        <RelationPanels
          source={{ type: 'Person', id: person.personId }}
          targets={['Business', 'Quotation', ...enabledPluginEntityTypes.map((et) => et.typeName), 'Person']}
          onRelationChanged={refreshRelations}
        />
        {/* Contatti fiera importati e collegati a questo Person (TS-100). */}
        {rapidContactsEnabled && <FairContactsPanel personId={person.personId} />}

        {rightBottomContributors.map((c) => (
          <c.component key={c.key} entityId={person.personId} />
        ))}
      </div>

      {/* Drawer per azioni rapide (telefono, task, nota): il TaskForm
          crea il task e, se FQN risolto, lo collega automaticamente a
          questa Person via Relationship bidirezionale. Il refresh delle
          relation fa apparire il task nella timeline. */}
      {quickDrawer && (() => {
        const personFqn = getEntityFqn('Person');
        return (
          <TaskForm
            mode="modal"
            initialType={quickDrawer.initialType}
            linkedEntity={personFqn ? { className: personFqn, classPK: person.personId } : undefined}
            onClose={() => setQuickDrawer(null)}
            onCreated={refreshRelations}
          />
        );
      })()}

      {/* NoteFormModal: stesso pattern di TaskForm modal, ma per l'entita'
          Note esplicita (TS-21 Macroarea 2). */}
      {noteFormOpen && (() => {
        const personFqn = getEntityFqn('Person');
        return (
          <NoteFormModal
            linkedEntity={personFqn ? { className: personFqn, classPK: person.personId } : undefined}
            onClose={() => setNoteFormOpen(false)}
            onCreated={refreshRelations}
          />
        );
      })()}

      {/* CallFormModal (TS-21 Macroarea 3). */}
      {callFormOpen && (() => {
        const personFqn = getEntityFqn('Person');
        return (
          <CallFormModal
            linkedEntity={personFqn ? { className: personFqn, classPK: person.personId } : undefined}
            onClose={() => setCallFormOpen(false)}
            onCreated={refreshRelations}
          />
        );
      })()}

      {/* LinkedinPostFormModal (TS-32): entità reale LinkedinPost. */}
      {linkedinFormOpen && (() => {
        const personFqn = getEntityFqn('Person');
        return (
          <LinkedinPostFormModal
            linkedEntity={personFqn ? { className: personFqn, classPK: person.personId } : undefined}
            onClose={() => setLinkedinFormOpen(false)}
            onCreated={refreshRelations}
          />
        );
      })()}

      {/* EmailFormModal — entita' Email come Call, no status. */}
      {emailFormOpen && (() => {
        const personFqn = getEntityFqn('Person');
        return (
          <EmailFormModal
            linkedEntity={personFqn ? { className: personFqn, classPK: person.personId } : undefined}
            onClose={() => setEmailFormOpen(false)}
            onCreated={refreshRelations}
          />
        );
      })()}

      {/* WhatsAppFormModal (TS-71): invio messaggio WhatsApp al contatto. Il
          link al contatto è fatto dal backend (personId), non via Relationship UI. */}
      {whatsAppFormOpen && (
        <WhatsAppFormModal
          personId={person.personId}
          phones={person.phones}
          onClose={() => setWhatsAppFormOpen(false)}
          onCreated={refreshWhatsAppSessions}
        />
      )}

      {/* MeetingFormModal (TS-21 Macroarea 4). */}
      {meetingFormOpen && (() => {
        const personFqn = getEntityFqn('Person');
        return (
          <MeetingFormModal
            linkedEntity={personFqn ? { className: personFqn, classPK: person.personId } : undefined}
            onClose={() => setMeetingFormOpen(false)}
            onCreated={refreshRelations}
          />
        );
      })()}

      {/* Modale edit del contatto (feature flag): riusa PersonForm
          in modalita' update. A salvataggio, ricarica il Person dal
          server — PersonForm al submit dispatcha updatePerson che
          aggiorna lo state, il Detail si rirenderizza. */}
      {editDrawerOpen && (
        <PersonForm
          personId={person.personId}
          onClose={() => setEditDrawerOpen(false)}
        />
      )}
      </div>
    </div>
  );
}

function InfoLine({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="info-row">
      <div className="info-row__label">{label}</div>
      <div className="info-row__value">{children}</div>
    </div>
  );
}
